import React, { useState, useEffect } from "react";
import styles from "../homepage.module.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Link } from "react-router-dom";

export default function Homepage() {
  const [marketData, setMarketData] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    totalMarketCap: 0,
    totalVolume: 0,
    btcDominance: 0,
    activeCryptos: 0,
    markets: 0,
  });
  const [stakingData, setStakingData] = useState([]);
  const [trendingCoins, setTrendingCoins] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoin, setSelectedCoin] = useState("bitcoin");
  const [timeframe, setTimeframe] = useState("7");
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Helper function for generating fallback colors
  const getColorForSymbol = (symbol) => {
    if (!symbol) return "#666666";
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
      "#F8C471",
      "#82E0AA",
    ];
    const index = symbol.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Updated getCoinIcon function - simplified to use CoinGecko's images
  const getCoinIcon = (coin) => {
    // If we have the coin object with image, use it directly
    if (coin && coin.image) {
      return coin.image;
    }

    // Fallback for coin ID only
    const coinId = typeof coin === "string" ? coin : coin?.id;

    // Use CoinGecko's asset URLs as fallback
    const fallbackIcons = {
      bitcoin: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
      ethereum:
        "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
      ripple:
        "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
      xrp: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
      tether: "https://assets.coingecko.com/coins/images/325/small/Tether.png",
      binancecoin:
        "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
      solana: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
      "usd-coin":
        "https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png",
      usdc: "https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png",
      cardano:
        "https://assets.coingecko.com/coins/images/975/small/cardano.png",
      polkadot:
        "https://assets.coingecko.com/coins/images/12171/small/polkadot.png",
      "wrapped-steth":
        "https://assets.coingecko.com/coins/images/18834/small/wstETH.png",
      wsteth:
        "https://assets.coingecko.com/coins/images/18834/small/wstETH.png",
      "staked-ether":
        "https://assets.coingecko.com/coins/images/18834/small/wstETH.png",
      cosmos:
        "https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png",
      tezos:
        "https://assets.coingecko.com/coins/images/976/small/Tezos-logo.png",
    };

    return (
      fallbackIcons[coinId] ||
      "https://assets.coingecko.com/coins/images/1/small/bitcoin.png"
    );
  };

  // Fetch live market data with error handling and retry logic
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        console.log("Fetching market data...");

        // Fetch top cryptocurrencies with detailed data
        const cryptoResponse = await fetch(
          "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=12&page=1&sparkline=true&price_change_percentage=1h,24h,7d&locale=en",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!cryptoResponse.ok) {
          throw new Error(`HTTP error! status: ${cryptoResponse.status}`);
        }

        const cryptoData = await cryptoResponse.json();
        console.log("Crypto data received:", cryptoData.length, "coins");

        // Fetch global market data
        const globalResponse = await fetch(
          "https://api.coingecko.com/api/v3/global",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!globalResponse.ok) {
          throw new Error(`HTTP error! status: ${globalResponse.status}`);
        }

        const globalData = await globalResponse.json();
        console.log("Global data received:", globalData);

        // Transform crypto data with proper icons
        const transformedData = cryptoData.map((coin) => ({
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol.toUpperCase(),
          price: coin.current_price,
          change1h: coin.price_change_percentage_1h_in_currency || 0,
          change24h: coin.price_change_percentage_24h || 0,
          change7d: coin.price_change_percentage_7d_in_currency || 0,
          volume: coin.total_volume,
          marketCap: coin.market_cap,
          image: coin.image,
          sparkline: coin.sparkline_in_7d?.price || [],
        }));

        setMarketData(transformedData);

        // Set global statistics
        if (globalData.data) {
          setGlobalStats({
            totalMarketCap: globalData.data.total_market_cap?.usd || 0,
            totalVolume: globalData.data.total_volume?.usd || 0,
            btcDominance: globalData.data.market_cap_percentage?.btc || 0,
            activeCryptos: globalData.data.active_cryptocurrencies || 0,
            markets: globalData.data.markets || 0,
          });
        }

        setLastUpdate(new Date());
        setLoading(false);
        console.log("Market data updated successfully");
      } catch (error) {
        console.error("Error fetching market data:", error);
        setLoading(false);

        // Set fallback data to show the UI still works (only if no data exists)
        setMarketData((prevData) => {
          if (prevData.length === 0) {
            return [
              {
                id: "bitcoin",
                name: "Bitcoin",
                symbol: "BTC",
                price: 43250.5,
                change1h: 0.5,
                change24h: 2.3,
                change7d: -1.2,
                volume: 25000000000,
                marketCap: 850000000000,
                sparkline: [],
              },
              {
                id: "ethereum",
                name: "Ethereum",
                symbol: "ETH",
                price: 2650.75,
                change1h: -0.2,
                change24h: 1.8,
                change7d: 3.4,
                volume: 15000000000,
                marketCap: 320000000000,
                sparkline: [],
              },
            ];
          }
          return prevData;
        });
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch chart data for selected coin
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        console.log(
          `Fetching chart data for ${selectedCoin}, ${timeframe} days`
        );

        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/${selectedCoin}/market_chart?vs_currency=usd&days=${timeframe}&interval=${
            timeframe === "1" ? "hourly" : "daily"
          }`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.prices && data.prices.length > 0) {
          const formattedData = data.prices.map(([timestamp, price]) => ({
            time: new Date(timestamp).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              ...(timeframe === "1" && { hour: "2-digit" }),
            }),
            price: parseFloat(price.toFixed(2)),
            timestamp: timestamp,
          }));

          setChartData(formattedData);
          console.log("Chart data updated:", formattedData.length, "points");
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        // Set sample chart data as fallback
        setChartData([
          {
            time: "Jan 1",
            price: 42000,
            timestamp: Date.now() - 6 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 2",
            price: 43000,
            timestamp: Date.now() - 5 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 3",
            price: 41500,
            timestamp: Date.now() - 4 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 4",
            price: 44000,
            timestamp: Date.now() - 3 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 5",
            price: 43500,
            timestamp: Date.now() - 2 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 6",
            price: 45000,
            timestamp: Date.now() - 1 * 24 * 60 * 60 * 1000,
          },
          { time: "Today", price: 43250, timestamp: Date.now() },
        ]);
      }
    };

    if (selectedCoin) {
      fetchChartData();
    }
  }, [selectedCoin, timeframe]);

  // Fetch staking data
  useEffect(() => {
    const fetchStakingData = async () => {
      try {
        console.log("Fetching staking data...");

        const stakingCoins = [
          "ethereum",
          "solana",
          "polkadot",
          "cardano",
          "cosmos",
          "tezos",
        ];
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${stakingCoins.join(
            ","
          )}&order=market_cap_desc&sparkline=false&price_change_percentage=24h`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Real staking APY rates (these would normally come from staking providers)
        const stakingRates = {
          ethereum: 4.2,
          solana: 6.8,
          polkadot: 11.5,
          cardano: 4.6,
          cosmos: 19.3,
          tezos: 5.9,
        };

        const stakingInfo = data.map((coin) => ({
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol.toUpperCase(),
          apy: stakingRates[coin.id] || 5.0,
          price: coin.current_price,
          change24h: coin.price_change_percentage_24h || 0,
          marketCap: coin.market_cap,
          image: coin.image,
        }));

        setStakingData(stakingInfo);
        console.log("Staking data updated:", stakingInfo.length, "coins");
      } catch (error) {
        console.error("Error fetching staking data:", error);
      }
    };

    fetchStakingData();
  }, []);

  // Fetch trending coins
  useEffect(() => {
    const fetchTrendingCoins = async () => {
      try {
        console.log("Fetching trending coins...");

        const response = await fetch(
          "https://api.coingecko.com/api/v3/search/trending",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.coins && data.coins.length > 0) {
          setTrendingCoins(data.coins.slice(0, 6));
          console.log("Trending data updated:", data.coins.length, "coins");
        }
      } catch (error) {
        console.error("Error fetching trending data:", error);
      }
    };

    fetchTrendingCoins();
  }, []);

  // Utility functions
  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
    return num.toLocaleString();
  };

  const formatPrice = (price) => {
    if (!price) return "$0.00";
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const selectedCoinData =
    marketData.find((coin) => coin.id === selectedCoin) || marketData[0];

  return (
    <div className={styles.homepage}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <Link to="/" className={styles.logoLink}>
              <div className={styles.logo}>KryptoPro</div>
            </Link>
            <nav className={styles.nav}>
              <Link to="/wallet" className={styles.navLink}>
                Trade
              </Link>
              <Link to="/wallet" className={styles.navLink}>
                Portfolio
              </Link>
              <Link to="/wallet" className={styles.navLink}>
                Staking
              </Link>
              <Link to="/wallet" className={styles.navLink}>
                NFTs
              </Link>
              <Link to="/learn" className={styles.navLink}>
                Learn
              </Link>
            </nav>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.marketStats}>
              <span>
                Market Cap:{" "}
                <strong>${formatNumber(globalStats.totalMarketCap)}</strong>
              </span>
              <span>
                24h Vol:{" "}
                <strong>${formatNumber(globalStats.totalVolume)}</strong>
              </span>
              <span>
                BTC Dom: <strong>{globalStats.btcDominance.toFixed(1)}%</strong>
              </span>
            </div>
            <Link to="/wallet" className={styles.primaryButton}>
              Connect Wallet
            </Link>
          </div>
        </div>
      </header>

      {/* Live Data Indicator */}
      <div className={styles.liveIndicator}>
        <div className={styles.liveIndicatorContent}>
          <div className={styles.liveStatus}>
            <div className={styles.liveDot}></div>
            <span>
              Live Data - Last Update: {lastUpdate.toLocaleTimeString()}
            </span>
          </div>
          <div className={styles.globalStats}>
            <span>
              Active Cryptos: {globalStats.activeCryptos.toLocaleString()}
            </span>
            <span>Markets: {globalStats.markets.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            Professional Cryptocurrency Trading Platform
          </h1>
          <p className={styles.heroSubtitle}>
            Advanced trading tools, real-time market data, and
            institutional-grade security. Trade with confidence on the most
            trusted crypto platform.
          </p>
          <div className={styles.heroButtons}>
            <Link to="/wallet" className={styles.primaryButton}>
              Start Trading Now
            </Link>
            <button className={styles.secondaryButton}>View All Markets</button>
          </div>
        </div>
        <div className={styles.heroBackground}></div>
      </section>

      {/* Live Market Data */}
      <section className={styles.marketSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Live Market Data</h2>
          <div className={styles.sectionControls}>
            <span className={styles.loadingIndicator}>
              {loading ? "Updating..." : `${marketData.length} coins loaded`}
            </span>
          </div>
        </div>
        <div className={styles.marketGrid}>
          {marketData.slice(0, 8).map((coin) => (
            <div
              key={coin.id}
              className={styles.marketCard}
              onClick={() => setSelectedCoin(coin.id)}
            >
              <div className={styles.coinHeader}>
                <div className={styles.coinIconContainer}>
                  <img
                    src={coin.image || getCoinIcon(coin)}
                    alt={`${coin.name} icon`}
                    className={styles.coinIcon}
                    onError={(e) => {
                      // Hide the broken image and show symbol fallback
                      e.target.style.display = "none";
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = "flex";
                    }}
                  />
                  <div
                    className={styles.coinSymbolFallback}
                    style={{
                      display: "none",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${getColorForSymbol(
                        coin.symbol
                      )} 0%, ${getColorForSymbol(coin.symbol)}CC 100%)`,
                      color: "white",
                      fontSize: "11px",
                      fontWeight: "bold",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                    }}
                  >
                    {coin.symbol?.substring(0, 3) || "?"}
                  </div>
                </div>

                <div className={styles.coinInfo}>
                  <span className={styles.coinName}>{coin.name}</span>
                  <span className={styles.coinSymbol}>{coin.symbol}</span>
                </div>
              </div>
              <div className={styles.priceInfo}>
                <span className={styles.price}>{formatPrice(coin.price)}</span>
                <div className={styles.priceChanges}>
                  <span
                    className={`${styles.change} ${
                      coin.change24h >= 0 ? styles.positive : styles.negative
                    }`}
                  >
                    24h: {coin.change24h >= 0 ? "+" : ""}
                    {coin.change24h.toFixed(2)}%
                  </span>
                  <span
                    className={`${styles.changeSmall} ${
                      coin.change1h >= 0 ? styles.positive : styles.negative
                    }`}
                  >
                    1h: {coin.change1h >= 0 ? "+" : ""}
                    {coin.change1h.toFixed(2)}%
                  </span>
                </div>
              </div>
              <div className={styles.marketInfo}>
                <div className={styles.marketStat}>
                  <span>Vol: ${formatNumber(coin.volume)}</span>
                </div>
                <div className={styles.marketStat}>
                  <span>MCap: ${formatNumber(coin.marketCap)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trading Chart Section */}
      <section className={styles.chartSection}>
        <h2 className={styles.sectionTitle}>Price Chart</h2>
        <div className={styles.tradingInterface}>
          <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
              <div className={styles.chartTitle}>
                <span>
                  {selectedCoinData?.name || "Bitcoin"} (
                  {selectedCoinData?.symbol || "BTC"})
                </span>
                <span className={styles.chartPrice}>
                  {formatPrice(selectedCoinData?.price)}
                </span>
                <span
                  className={`${styles.chartChange} ${
                    selectedCoinData?.change24h >= 0
                      ? styles.positive
                      : styles.negative
                  }`}
                >
                  {selectedCoinData?.change24h >= 0 ? "+" : ""}
                  {selectedCoinData?.change24h?.toFixed(2)}%
                </span>
              </div>
              <div className={styles.timeframes}>
                <button
                  className={`${styles.timeframe} ${
                    timeframe === "1" ? styles.active : ""
                  }`}
                  onClick={() => setTimeframe("1")}
                >
                  1D
                </button>
                <button
                  className={`${styles.timeframe} ${
                    timeframe === "7" ? styles.active : ""
                  }`}
                  onClick={() => setTimeframe("7")}
                >
                  7D
                </button>
                <button
                  className={`${styles.timeframe} ${
                    timeframe === "30" ? styles.active : ""
                  }`}
                  onClick={() => setTimeframe("30")}
                >
                  1M
                </button>
              </div>
            </div>
            <div className={styles.chart}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                    <XAxis
                      dataKey="time"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#888" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#888" }}
                      domain={["dataMin", "dataMax"]}
                      tickFormatter={(value) => `$${formatNumber(value)}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1a1a1a",
                        border: "1px solid #333",
                        borderRadius: "8px",
                        color: "#fff",
                      }}
                      formatter={(value) => [formatPrice(value), "Price"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="#00d4aa"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: "#00d4aa" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.chartLoading}>Loading chart data...</div>
              )}
            </div>
          </div>

          {/* Coin Selection Panel - Updated with proper icons */}
          <div className={styles.coinSelector}>
            <h3 className={styles.coinSelectorTitle}>Select Coin</h3>
            <div className={styles.coinList}>
              {marketData.slice(0, 6).map((coin) => (
                <div
                  key={coin.id}
                  className={`${styles.coinListItem} ${
                    selectedCoin === coin.id ? styles.selected : ""
                  }`}
                  onClick={() => setSelectedCoin(coin.id)}
                >
                  <div className={styles.coinIconContainer}>
                    <img
                      src={coin.image || getCoinIcon(coin)}
                      alt={`${coin.name} icon`}
                      className={styles.coinIcon}
                      style={{ width: "24px", height: "24px" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                    <div
                      className={styles.coinSymbolFallback}
                      style={{
                        display: "none",
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${getColorForSymbol(
                          coin.symbol
                        )} 0%, ${getColorForSymbol(coin.symbol)}CC 100%)`,
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {coin.symbol?.substring(0, 2) || "?"}
                    </div>
                  </div>
                  <div className={styles.coinDetails}>
                    <span className={styles.coinName}>{coin.symbol}</span>
                    <span className={styles.coinPrice}>
                      {formatPrice(coin.price)}
                    </span>
                  </div>
                  <span
                    className={`${styles.coinChange} ${
                      coin.change24h >= 0 ? styles.positive : styles.negative
                    }`}
                  >
                    {coin.change24h >= 0 ? "+" : ""}
                    {coin.change24h.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Staking Section - Updated with proper icons */}
      <section className={styles.stakingSection}>
        <h2 className={styles.sectionTitle}>Staking Opportunities</h2>
        <div className={styles.stakingGrid}>
          {stakingData.slice(0, 6).map((stake) => (
            <div key={stake.id} className={styles.stakingCard}>
              <div className={styles.stakingHeader}>
                <div className={styles.stakingCoinInfo}>
                  <div className={styles.coinIconContainer}>
                    <img
                      src={stake.image || getCoinIcon(stake)}
                      alt={`${stake.name} icon`}
                      className={styles.coinIcon}
                      style={{ width: "28px", height: "28px" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                    <div
                      className={styles.coinSymbolFallback}
                      style={{
                        display: "none",
                        width: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${getColorForSymbol(
                          stake.symbol
                        )} 0%, ${getColorForSymbol(stake.symbol)}CC 100%)`,
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {stake.symbol?.substring(0, 2) || "?"}
                    </div>
                  </div>
                  <span className={styles.stakingCoin}>{stake.symbol}</span>
                </div>
                <span className={styles.stakingApy}>{stake.apy}% APY</span>
              </div>
              <div className={styles.stakingDetails}>
                <div className={styles.stakingPrice}>
                  <span>Price: {formatPrice(stake.price)}</span>
                  <span
                    className={`${styles.stakingChange} ${
                      stake.change24h >= 0 ? styles.positive : styles.negative
                    }`}
                  >
                    {stake.change24h >= 0 ? "+" : ""}
                    {stake.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className={styles.stakingMarketCap}>
                  Market Cap: ${formatNumber(stake.marketCap)}
                </div>
              </div>
              <Link to="/wallet">
                <button className={styles.stakeButton}>
                  Stake {stake.symbol}
                </button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Trending Section - Updated with proper icons */}
      <section className={styles.trendingSection}>
        <h2 className={styles.sectionTitle}>Trending Cryptocurrencies</h2>
        <div className={styles.trendingGrid}>
          {trendingCoins.map((coin, index) => (
            <div key={coin.item.id} className={styles.trendingCard}>
              <div className={styles.trendingRank}>#{index + 1}</div>
              <div className={styles.trendingInfo}>
                <div className={styles.trendingCoinHeader}>
                  <div className={styles.coinIconContainer}>
                    <img
                      src={coin.item.large || getCoinIcon(coin.item)}
                      alt={`${coin.item.name} icon`}
                      className={styles.coinIcon}
                      style={{ width: "32px", height: "32px" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                    <div
                      className={styles.coinSymbolFallback}
                      style={{
                        display: "none",
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${getColorForSymbol(
                          coin.item.symbol
                        )} 0%, ${getColorForSymbol(coin.item.symbol)}CC 100%)`,
                        color: "white",
                        fontSize: "11px",
                        fontWeight: "bold",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {coin.item.symbol?.substring(0, 3) || "?"}
                    </div>
                  </div>
                  <div>
                    <h3 className={styles.trendingName}>{coin.item.name}</h3>
                    <span className={styles.trendingSymbol}>
                      {coin.item.symbol}
                    </span>
                  </div>
                </div>
                <div className={styles.trendingRankInfo}>
                  Market Cap Rank: #{coin.item.market_cap_rank || "N/A"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerSection}>
            <div className={styles.footerLogo}>
              <h3>Krypropro</h3>
              <p>
                Professional cryptocurrency trading platform with advanced tools
                and real-time data.
              </p>
            </div>
          </div>

          <div className={styles.footerSection}>
            <h4>Products</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to="/wallet">Spot Trading</Link>
              </li>
              <li>
                <Link to="/wallet">Futures</Link>
              </li>
              <li>
                <Link to="/wallet">Options</Link>
              </li>
              <li>
                <Link to="/wallet">Staking</Link>
              </li>
              <li>
                <Link to="/wallet">NFT Marketplace</Link>
              </li>
            </ul>
          </div>

          <div className={styles.footerSection}>
            <h4>Company</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to="/about">About Us</Link>
              </li>
              <li>
                <Link to="/careers">Careers</Link>
              </li>
              <li>
                <Link to="/security">Security</Link>
              </li>
              <li>
                <Link to="/legal">Legal</Link>
              </li>
              <li>
                <Link to="/support">Support</Link>
              </li>
            </ul>
          </div>

          <div className={styles.footerSection}>
            <h4>Resources</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to="/api">API Documentation</Link>
              </li>
              <li>
                <Link to="/fees">Fee Structure</Link>
              </li>
              <li>
                <Link to="/learn">Learning Center</Link>
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              <li>
                <Link to="/status">System Status</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.footerCopyright}>
            © 2024 Krypropro. All rights reserved.
          </div>
          <div className={styles.footerLegal}>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/risk">Risk Disclosure</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
